// Global
import React from 'react';
import { defaultConfig, tv } from 'tailwind-variants';

// Local
import { SiteStructure } from '.generated/Feature.CW.model';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { Data } from '.generated/Foundation.CW.model';
import { getTestProps } from 'lib/testing/utils';
import classNames from 'classnames';
import { SvgIcon } from 'helpers/SvgIcon';
import { useCurrentScreenType } from 'lib/utils/get-screen-type';
import { withStandardComponentWrapper } from 'helpers/HOC';
import { ComponentProps } from 'lib/component-props';
import { LinkField } from '@sitecore-jss/sitecore-jss-nextjs';
import { useRouter } from 'next/router';

// Tailwind Styles
defaultConfig.twMerge = false;

type FooterColumnsProps = SiteStructure.Footer.FooterColumn_Item & {
  fields?: {
    columnLinks?: Array<Data.Links.GenericLink>;
  };
};

export type FooterProps = ComponentProps &
  Omit<SiteStructure.Footer.Footer, 'contactUsButton'> & {
    fields?: {
      contactUsButton?: LinkField;
      footerColumns?: Array<FooterColumnsProps>;
    };
  };

const Footernavlinks = ({ linksGroup }: { linksGroup: FooterColumnsProps }) => {
  const extendedTailwindVariants = tv({
    extend: TAILWIND_VARIANTS,
  });
  const links = linksGroup?.fields?.columnLinks as Data.Links.GenericLink_Item[];
  const { linkListItem, linkListLink, linkListTitle } = extendedTailwindVariants();

  const { currentScreenWidth } = useCurrentScreenType();

  const [isActiveGroup, setIsActiveGroup] = React.useState<boolean>(
    currentScreenWidth != undefined && currentScreenWidth > 1023
  );
  const contentRef = React.useRef<HTMLUListElement>(null);

  React.useEffect(() => {
    if (currentScreenWidth > 1023) {
      setIsActiveGroup(!(currentScreenWidth > 0 && currentScreenWidth <= 1023));
    } else {
      setIsActiveGroup(false);
    }
  }, [currentScreenWidth]);

  return (
    <div
      className={classNames(
        'col-span-1 group',
        currentScreenWidth && currentScreenWidth <= 1023
          ? 'border-b-2 border-gray-500 first:border-t-2'
          : ''
      )}
    >
      <button
        className="!w-full group text-left lg:pointer-events-none"
        tabIndex={currentScreenWidth <= 1023 ? 0 : -1}
        onClick={currentScreenWidth <= 1023 ? () => setIsActiveGroup(!isActiveGroup) : undefined}
      >
        <div
          className={classNames(
            linkListItem(),
            '!py-9 lg:!pt-0 lg:!pb-6',
            currentScreenWidth <= 1023 && 'group-focus:bg-blue-100'
          )}
        >
          <RichTextWrapper
            className={linkListTitle()}
            field={linksGroup?.fields?.linkLabelText}
            tag="div"
          />
          {currentScreenWidth ? (
            currentScreenWidth <= 1023 && (
              <SvgIcon
                icon={!isActiveGroup ? 'plus' : 'minus'}
                viewBox={!isActiveGroup ? '0 0 24 24' : '0 0 21 5'}
                size="lg"
                className={classNames(
                  'text-blue-base transition-all duration-300 rotate-0',
                  !isActiveGroup ? 'rotate-90' : 'rotate-0'
                )}
              />
            )
          ) : (
            <></>
          )}
        </div>
      </button>
      <ul
        ref={contentRef}
        key={linksGroup?.id}
        className={classNames('transition-all duration-300 overflow-hidden max-h-0')}
        style={{
          maxHeight: isActiveGroup ? `${contentRef.current?.scrollHeight}px` : '0px',
        }}
      >
        {links?.map((link) => (
          <li className={classNames(linkListItem(), 'last:mb-8')} key={link?.id as string}>
            <LinkWrapper
              tabIndex={isActiveGroup ? 0 : -1}
              className={linkListLink()}
              field={link?.fields?.link}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export const Footer = (props: FooterProps): JSX.Element => {
  const { RenderingIdentifier } = props?.params || {};
  const router = useRouter();
  const footerColumns = props.fields?.footerColumns as SiteStructure.Footer.FooterColumn_Item[];
  const footerLogo = props.fields?.footerLogo;
  const footerLogoLink = props.fields?.footerLogoLink;

  // Detect private mode
  const isPrivateMode = router?.query?.private === 'true';

  /*
   * Rendering
   */

  if (!props.fields) {
    return <></>;
  }

  const extendedTailwindVariants = tv({
    extend: TAILWIND_VARIANTS,
  });

  const { base, contentContainer, logoContainer } = extendedTailwindVariants();

  return !isPrivateMode ? (
    <div
      data-component="authorable/shared/site-structure/footer/footer"
      className={base()}
      id={RenderingIdentifier}
    >
      <div className={contentContainer()}>
        <div className="flex gap-y-16 gap-x-24 flex-wrap lg:flex-nowrap flex-col lg:flex-row">
          <div {...getTestProps(`footer-logo-wrapper`)} className={logoContainer()}>
            <div className="flex flex-row justify-between lg:flex-col gap-y-16 gap-x-6">
              <LinkWrapper field={footerLogoLink} className="inline-block">
                <ImageWrapper field={footerLogo} className="w-[162px] lg:w-[238px]" />
              </LinkWrapper>
              <RichTextWrapper
                field={props?.fields?.addressText}
                className="!text-14 [&_p]:!text-14 font-normal text-gray-900 !font-centuryGothic [&_p]:!font-centuryGothic"
              />
            </div>

            <LinkWrapper
              field={props?.fields?.contactUsButton}
              ctaVariant="secondary"
              className="w-full lg:w-fit leading-[14px] inline-block text-center lg:inline-block !py-[11.5px] [&_span]:leading-[21px] [&_span]:block"
            />
            <RichTextWrapper
              field={props?.fields?.mcwAffiliation}
              className="[&_a]:inline [&_a]:no-underline hover:[&_a]:!underline hover:[&_a]:decoration-1 font-normal tracking-[0.25px] text-gray-900 [&_a]:!text-blue-700 hover:[&_a]:underline-offset-4 [&_p]:!font-centuryGothic [&_a]:!font-centuryGothic [&_a]:!text-14 [&_p]:!text-14 font-centuryGothic [&_svg]:!hidden [&_.svg-icon]:!hidden"
            />
          </div>

          <div
            className={classNames(
              'flex-grow grid grid-cols-1 lg:grid-cols-3 lg:gap-y-16 lg:gap-x-24',
              footerColumns.length === 2 && 'lg:!grid-cols-2',
              footerColumns.length === 1 && 'lg:!grid-cols-1'
            )}
          >
            {footerColumns?.map((groupLabel, index) => (
              <Footernavlinks key={index} linksGroup={groupLabel} />
            ))}
          </div>
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};

export const Default = withStandardComponentWrapper(Footer);

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['theme-lightblue', 'bg-theme-bg'],
    contentContainer: [
      'flex',
      'flex-col',
      'm-auto',
      'w-full',
      'max-w-screen-xxl',
      'pt-16 pb-8',
      'px-10 lg:px-20',
      'print:hidden',
    ],
    linkListItem: ['py-7', 'flex', 'justify-between', 'gap-6', 'px-2'],
    linkListLink: [
      'capitalize',
      '!font-bold',
      'font-centuryGothic',
      'text-14',
      'text-blue-700',
      'hover:underline',
      'hover:underline-offset-4',
      'tracking-[0.25px]',
    ],
    linkListTitle: ['capitalize', 'font-bold', 'font-centuryGothic', 'text-gray-900'],
    logoContainer: ['w-full lg:w-[328px]', 'flex', 'flex-col', 'gap-16'],
  },
});
